import { useCallback, useMemo } from 'react';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { CallResult } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { PhoneRecord } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { UsersQueries } from '@frontend/api-users';
import { useTranslation } from '@frontend/i18n';
import { emitOpenCallInsightPanel, emitOpenVoicemailInsightPanel } from '@frontend/inbox-call-item-event-bus';
import { getFullName } from '@frontend/name';
import { Photos } from '@frontend/photos';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Avatar, Chat } from '@frontend/design-system';
import { ShareInMessageVirtuosoContext } from '../../types';

type VoiceBubbleProps = {
  voiceItem: PhoneRecord;
  context: ShareInMessageVirtuosoContext;
};

type BubbleInfo = {
  title: string;
  subtitle?: string;
  resultKey?: 'missed' | 'inbound' | 'outbound' | 'abandoned' | 'voicemail';
};

export const VoiceBubble = ({ voiceItem, context: { personInThreadContext, person, optedOut } }: VoiceBubbleProps) => {
  const isInbound = voiceItem.direction === Direction.DIRECTION_INBOUND;

  const { t } = useTranslation('integrated-messaging');
  const { accessibleLocationIds } = useAppScopeStore();

  const users = UsersQueries.useGetMultiLocationsUsers({
    locationIds: accessibleLocationIds,
    params: { showChildren: true },
  }).flatMap(({ data }) => data || []);

  // personInThreadContext is for when thread body loads messages from different contact (custom number) while the header and sending context doesn't change.
  const inboundPatientName = personInThreadContext
    ? getFullName(personInThreadContext)
    : person
    ? getFullName(person)
    : undefined;

  const userSender = users.find((user) => user.UserID === voiceItem.createdBy);
  const userAvatarTitle = userSender ? getFullName(userSender) : undefined;
  const avatar = isInbound ? (
    <Photos.ContactProfilePhoto
      title={!inboundPatientName ? t('Unknown User') : undefined}
      disableClick
      personId={personInThreadContext?.personId ?? person?.personId ?? voiceItem.personId ?? ''}
      locationId={voiceItem.locationId}
      name={inboundPatientName || undefined}
      size='small'
    />
  ) : (
    <Avatar title={userAvatarTitle ?? t('Unknown User')} name={userAvatarTitle} size='small' isUser />
  );
  const timestamp = new Date(voiceItem.startedAt || '');
  const deleter = voiceItem.deletedAt ? users.find((user) => user.UserID === voiceItem.deletedBy) : undefined;
  const deletedBodyText = voiceItem.deletedAt
    ? t('This message was deleted on {{date}} by {{name}}', {
        date: new Date(voiceItem.deletedAt).toLocaleDateString(),
        name: deleter ? getFullName(deleter) : t('Unknown User'),
      })
    : undefined;

  const bubbleInfo: BubbleInfo = useMemo(() => {
    const callResult = voiceItem.callResult;

    switch (callResult) {
      case CallResult.CALL_RESULT_ANSWERED:
        return isInbound
          ? {
              title: t('Inbound Call'),
              subtitle: inboundPatientName || 'Anonymous user',
              resultKey: 'inbound',
            }
          : { title: t('Outbound Call'), resultKey: 'outbound' };
      case CallResult.CALL_RESULT_ABANDONED:
        return { title: t('Abandoned Call'), resultKey: 'abandoned' };
      case CallResult.CALL_RESULT_MISSED:
        return { title: t('Missed Call'), resultKey: 'missed' };
      case CallResult.CALL_RESULT_VOICEMAIL:
        return { title: t('Voicemail'), resultKey: 'voicemail' };
      default:
        return { title: t('Unknown Call') };
    }
  }, [voiceItem.callResult]);

  const isVoiceItemDeleted = voiceItem.deletedBy && voiceItem.deletedBy && deleter;

  const handleCallAction = useCallback(() => {
    if (bubbleInfo.resultKey === 'voicemail') {
      emitOpenVoicemailInsightPanel({
        voicemailId: voiceItem.channelId,
        locationId: voiceItem.locationId,
      });
    } else {
      emitOpenCallInsightPanel({
        channelId: voiceItem.channelId,
        includeCallIntelligence: true,
        locationId: voiceItem.locationId,
      });
    }
  }, [
    emitOpenCallInsightPanel,
    emitOpenVoicemailInsightPanel,
    bubbleInfo.resultKey,
    voiceItem.channelId,
    voiceItem.locationId,
  ]);

  return (
    <Chat
      direction={isInbound ? 'inbound' : 'outbound'}
      avatar={avatar}
      timestamp={timestamp}
      css={{
        padding: theme.spacing(0, 2),
        opacity: optedOut ? 0.5 : 1,
      }}
    >
      {isVoiceItemDeleted ? (
        <Chat.Bubble variant={'deleted'} text={deletedBodyText} />
      ) : (
        <Chat.Bubble
          variant='call'
          callResult={bubbleInfo.resultKey}
          callProps={{
            callResult: bubbleInfo.resultKey,
            callAction: handleCallAction,
          }}
          text={bubbleInfo.title}
          subText={bubbleInfo.subtitle}
          key={voiceItem.channelId}
          maxWidth={202}
          styles={{
            backgroundColor: theme.colors.white,
            borderRadius: theme.spacing(1),
            outline: `0.50px solid ${theme.colors.neutral20}`,
            '&:hover': {
              backgroundColor: theme.colors.neutral5,
            },
          }}
        />
      )}
    </Chat>
  );
};
